import { Pattern } from './Pattern';
import { PatternType, Difficulty, Bounds, Vector2 } from '../types';

/**
 * PulseRing - Concentric expanding rings of dots spawn at random positions
 * and expand outward, like ripples in a pond. Player must dodge between rings
 * or stay inside the safe zone at the center of each new ripple.
 */
export class PulseRing extends Pattern {
  readonly type = PatternType.PULSE_RING;
  difficulty: Difficulty = Difficulty.MEDIUM;

  private readonly duration: number = 16000;
  private ringSpawnInterval: number = 2200;
  private timeSinceLastRing: number = 0;
  private readonly ringDotCount: number = 22;
  private readonly ringExpansionSpeed: number = 90;
  private readonly ringGapCount: number = 1; // gap in the ring so it's escapable
  private readonly gapArcDots: number = 3;
  private maxRadius: number = 0;

  // Each active ring tracks its center and current radius
  private rings: Array<{ cx: number; cy: number; radius: number }> = [];

  constructor(difficulty: Difficulty = Difficulty.MEDIUM) {
    super();
    this.difficulty = difficulty;
    switch (difficulty) {
      case Difficulty.HARD:
        this.ringSpawnInterval = 1600;
        break;
      case Difficulty.MEDIUM:
        this.ringSpawnInterval = 2200;
        break;
      default:
        this.ringSpawnInterval = 3000;
    }
  }

  spawn(_center: Vector2, bounds: Bounds): void {
    this.start();
    this.maxRadius = Math.sqrt(bounds.width * bounds.width + bounds.height * bounds.height);
    this.timeSinceLastRing = this.ringSpawnInterval; // spawn first ring immediately
  }

  update(dt: number, playerPosition: Vector2, bounds: Bounds): void {
    if (this.elapsedMs <= this.duration) {
      this.timeSinceLastRing += dt * 1000;
      if (this.timeSinceLastRing >= this.ringSpawnInterval) {
        this.timeSinceLastRing = 0;
        this.spawnNewRing(bounds);
      }
    }

    // Expand all active rings and spawn dots on their leading edge
    for (let i = this.rings.length - 1; i >= 0; i--) {
      const ring = this.rings[i];
      const prevRadius = ring.radius;
      ring.radius += this.ringExpansionSpeed * dt;

      // Spawn a "shell" of dots at the new radius if it crossed a spacing threshold
      const SPACING = 24;
      if (Math.floor(ring.radius / SPACING) > Math.floor(prevRadius / SPACING)) {
        this.spawnRingShell(ring.cx, ring.cy, ring.radius);
      }

      if (ring.radius > this.maxRadius + 100) {
        this.rings.splice(i, 1);
      }
    }

    // Update all dots (they fly outward from ring center)
    for (let i = this.dots.length - 1; i >= 0; i--) {
      const dot = this.dots[i];
      if (dot.isFrozen()) {
        dot.update(dt, bounds, playerPosition);
        continue;
      }
      const pos = dot.getPosition();
      // Off-screen culling
      if (pos.x < -100 || pos.x > bounds.width + 100 ||
          pos.y < -100 || pos.y > bounds.height + 100) {
        this.dots.splice(i, 1);
        continue;
      }
      dot.update(dt, bounds, playerPosition);
    }
  }

  private spawnNewRing(bounds: Bounds): void {
    // Spawn somewhere in the arena (not too close to player ideally)
    const margin = 100;
    const cx = margin + Math.random() * (bounds.width - margin * 2);
    const cy = margin + Math.random() * (bounds.height - margin * 2);
    this.rings.push({ cx, cy, radius: 10 });
  }

  private spawnRingShell(cx: number, cy: number, radius: number): void {
    // Pick a random gap position so each shell has a hole
    const gapStart = Math.floor(Math.random() * this.ringDotCount);
    const gapSet = new Set<number>();
    for (let g = 0; g < this.gapArcDots; g++) {
      gapSet.add((gapStart + g) % this.ringDotCount);
    }

    for (let i = 0; i < this.ringDotCount; i++) {
      if (this.ringGapCount > 0 && gapSet.has(i)) continue;
      const angle = (i / this.ringDotCount) * Math.PI * 2;
      const x = cx + Math.cos(angle) * radius;
      const y = cy + Math.sin(angle) * radius;
      // Outward velocity
      const vx = Math.cos(angle) * this.ringExpansionSpeed;
      const vy = Math.sin(angle) * this.ringExpansionSpeed;
      const dot = this.spawnDot(x, y, { x: vx, y: vy });
      dot.skipSpawnAnimation();
    }
  }

  isComplete(): boolean {
    return this.elapsedMs > this.duration && this.dots.length === 0 && this.rings.length === 0;
  }

  isActivelySpawning(): boolean {
    return this.elapsedMs <= this.duration;
  }
}
